import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * ResponsiveTable — luxe-themed table that:
 *  - Desktop (md+): renders a <table> with sticky header
 *  - Mobile (<md): renders stacked cards built from the same column metadata
 *  - Built-in client-side pagination (10/25/50)
 *
 * Props:
 *  columns: [
 *    {
 *      key:           string                    // unique key
 *      label:         string                    // header text
 *      cell:          (row) => ReactNode        // cell renderer
 *      headerClass?:  string                    // <th> className additions
 *      cellClass?:    string                    // <td> className additions
 *      align?:        "left" | "right" | "center"
 *      hideOnMobile?: boolean                   // omit on mobile card body
 *      mobileLabel?:  string                    // overrides label when shown on card
 *    }
 *  ]
 *  rows:        array
 *  rowKey:      (row) => string
 *  testid?:     string                          // applied to tbody / cards container
 *  mobileCard?: (row) => ({ title, subtitle?, actions?, fieldsOverride? })
 *               // If omitted, mobile card uses every non-hidden column as key/value rows
 *  rowActions?: (row) => ReactNode              // shared actions slot, last desktop column
 *  emptyMessage?: string
 *  defaultPageSize?: 10 | 25 | 50
 *  paginated?:  boolean                         // default true
 *  rowClassName?: (row) => string
 */
export default function ResponsiveTable({
  columns,
  rows,
  rowKey,
  testid,
  mobileCard,
  rowActions,
  emptyMessage = "No records found.",
  defaultPageSize = 25,
  paginated = true,
  rowClassName,
}) {
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [page, setPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil((rows?.length || 0) / pageSize));
  const safePage = Math.min(page, totalPages);
  const pagedRows = useMemo(() => {
    if (!paginated) return rows || [];
    const start = (safePage - 1) * pageSize;
    return (rows || []).slice(start, start + pageSize);
  }, [rows, safePage, pageSize, paginated]);

  const showingFrom = rows?.length ? (safePage - 1) * pageSize + 1 : 0;
  const showingTo = Math.min(safePage * pageSize, rows?.length || 0);

  return (
    <div className="glass-card overflow-hidden" data-testid={testid ? `${testid}-wrapper` : undefined}>
      {/* DESKTOP TABLE */}
      <div className="hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full text-sm table-fixed">
            <colgroup>
              {columns.map((c) => (
                <col key={c.key} style={c.width ? { width: c.width } : undefined} />
              ))}
              {rowActions && <col style={{ width: "1%" }} />}
            </colgroup>
            <thead className="sticky top-0 z-10 bg-[#0F0F0F]">
              <tr className="text-left overline text-[0.65rem] border-b border-white/10">
                {columns.map((c) => (
                  <th
                    key={c.key}
                    className={`px-5 py-4 font-normal whitespace-nowrap ${c.align === "right" ? "text-right" : c.align === "center" ? "text-center" : ""} ${c.headerClass || ""}`}
                  >
                    {c.label}
                  </th>
                ))}
                {rowActions && <th className="px-5 py-4" />}
              </tr>
            </thead>
            <tbody data-testid={testid}>
              {pagedRows.length === 0 && (
                <tr>
                  <td colSpan={columns.length + (rowActions ? 1 : 0)} className="px-6 py-16 text-center text-[#8A8A8A]">
                    {emptyMessage}
                  </td>
                </tr>
              )}
              {pagedRows.map((row) => (
                <tr
                  key={rowKey(row)}
                  className={`border-b border-white/5 hover:bg-white/[0.015] transition-colors ${rowClassName ? rowClassName(row) : ""}`}
                  data-testid={testid ? `${testid}-row` : undefined}
                >
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      className={`px-5 py-4 align-top ${c.align === "right" ? "text-right" : c.align === "center" ? "text-center" : ""} ${c.cellClass || "truncate"}`}
                      title={typeof row[c.key] === "string" ? row[c.key] : undefined}
                    >
                      {c.cell(row)}
                    </td>
                  ))}
                  {rowActions && (
                    <td className="px-5 py-4 text-right whitespace-nowrap">
                      <div className="inline-flex items-center gap-3 justify-end">
                        {rowActions(row)}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* MOBILE CARDS */}
      <div className="md:hidden divide-y divide-white/5" data-testid={testid ? `${testid}-mobile` : undefined}>
        {pagedRows.length === 0 && (
          <div className="px-5 py-16 text-center text-[#8A8A8A] text-sm">{emptyMessage}</div>
        )}
        {pagedRows.map((row) => (
          <MobileCard
            key={rowKey(row)}
            row={row}
            columns={columns}
            mobileCard={mobileCard}
            rowActions={rowActions}
            testid={testid}
          />
        ))}
      </div>

      {/* PAGINATION CONTROLS */}
      {paginated && (rows?.length || 0) > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-5 py-3 border-t border-white/5 bg-[#0A0A0A]/50">
          <div className="flex items-center gap-3 text-[0.65rem] mono text-[#8A8A8A]">
            <span>Showing {showingFrom}–{showingTo} of {rows.length}</span>
            <select
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
              className="luxe-input px-2 py-1 text-[0.65rem] mono"
              data-testid={testid ? `${testid}-pagesize` : undefined}
              aria-label="Rows per page"
            >
              {[10, 25, 50].map((n) => <option key={n} value={n}>{n}/page</option>)}
            </select>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage <= 1}
              className="min-h-[44px] sm:min-h-0 px-3 py-2 text-xs text-[#D4AF37] hover:bg-white/5 disabled:text-[#444] disabled:hover:bg-transparent inline-flex items-center gap-1"
              data-testid={testid ? `${testid}-prev` : undefined}
            >
              <ChevronLeft className="w-3.5 h-3.5" /> Prev
            </button>
            <span className="text-[0.65rem] mono text-[#8A8A8A] px-3" data-testid={testid ? `${testid}-page-info` : undefined}>
              {safePage} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage >= totalPages}
              className="min-h-[44px] sm:min-h-0 px-3 py-2 text-xs text-[#D4AF37] hover:bg-white/5 disabled:text-[#444] disabled:hover:bg-transparent inline-flex items-center gap-1"
              data-testid={testid ? `${testid}-next` : undefined}
            >
              Next <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function MobileCard({ row, columns, mobileCard, rowActions, testid }) {
  const [expanded, setExpanded] = useState(false);

  const meta = mobileCard ? mobileCard(row) : {};
  const visibleColumns = columns.filter((c) => !c.hideOnMobile);
  // First column header => default title fallback
  const titleNode = meta.title ?? columns[0]?.cell(row);
  const subtitleNode = meta.subtitle ?? null;

  // Build key/value rows from the explicit `fields` override OR remaining columns
  let fields;
  if (meta.fields) {
    fields = meta.fields;
  } else if (meta.fieldsOverride) {
    fields = meta.fieldsOverride;
  } else {
    fields = visibleColumns.slice(1).map((c) => ({
      label: c.mobileLabel || c.label,
      value: c.cell(row),
    }));
  }

  // On mobile, show first 2 fields by default; rest under "View details"
  const collapsed = fields.slice(0, 2);
  const rest = fields.slice(2);

  return (
    <div className="px-5 py-4" data-testid={testid ? `${testid}-card` : undefined}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-sm truncate">{titleNode}</div>
          {subtitleNode && (
            <div className="text-[0.65rem] mono text-[#8A8A8A] mt-0.5 truncate">{subtitleNode}</div>
          )}
        </div>
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
        {collapsed.map((f, i) => (
          <div key={f.label || `c-${i}`} className="min-w-0">
            <dt className="overline text-[0.55rem] text-[#7a7a7a]">{f.label}</dt>
            <dd className="mt-0.5 truncate">{f.value}</dd>
          </div>
        ))}
        {expanded && rest.map((f, i) => (
          <div key={f.label || `r-${i}`} className="min-w-0">
            <dt className="overline text-[0.55rem] text-[#7a7a7a]">{f.label}</dt>
            <dd className="mt-0.5 truncate">{f.value}</dd>
          </div>
        ))}
      </dl>
      {rest.length > 0 && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-3 text-[0.65rem] uppercase tracking-wider text-[#D4AF37] min-h-[44px] inline-flex items-center"
          data-testid={testid ? `${testid}-card-toggle` : undefined}
        >
          {expanded ? "Hide details" : `View details (+${rest.length})`}
        </button>
      )}
      {(meta.actions || rowActions) && (
        <div className="mt-3 flex flex-wrap gap-2">
          {meta.actions || rowActions(row)}
        </div>
      )}
    </div>
  );
}
